// GAVION « Forfait proposé » on a deal (supabase/schemas/76_gavion_deal_package.sql, owner request 2026-10-21): the TS
// twin of public.deals_proposal_price (the price snapshot and the MRR that becomes the deal's value) and of the masking
// (private.deal_proposal_columns). Used by the app (FakeRest demo, the deal form's preview) and the edge functions
// (automation templates never print the proposal, like the amount).
import {
  annualAmount,
  annualMrr,
  monthlyTotal,
  type AddonSnapshot,
  type Billing,
} from "./commissions.ts";

/** The columns masked like the amount (private.deal_proposal_columns; opsParity). */
export const DEAL_PROPOSAL_COLUMNS = [
  "proposed_package_id",
  "proposed_billing",
  "proposed_addons",
  "proposed_package_price",
  "proposed_discount_pct",
  "proposed_mrr",
] as const;
export type DealProposalColumn = (typeof DEAL_PROPOSAL_COLUMNS)[number];

export type DealProposal = {
  proposed_package_id: number | null;
  proposed_billing: Billing | null;
  proposed_addons: AddonSnapshot[];
  proposed_package_price: number | null;
  proposed_discount_pct: number | null;
  proposed_mrr: number | null;
};
/** A stored deal row as read (ids may be strings, a masked row has nulls). */
export type StoredProposal = {
  proposed_package_id?: unknown;
  proposed_billing?: unknown;
  proposed_addons?: readonly AddonSnapshot[] | null;
  proposed_package_price?: unknown;
  proposed_discount_pct?: unknown;
  proposed_mrr?: unknown;
  stage?: unknown;
};
export const NO_PROPOSAL: DealProposal = {
  proposed_package_id: null,
  proposed_billing: null,
  proposed_addons: [],
  proposed_package_price: null,
  proposed_discount_pct: null,
  proposed_mrr: null,
};

/** The fields of a row with the proposal blanked (deals_visible / deal_visible_json for a Setter / Apporteur only). */
export const withoutProposal = <T extends Record<string, unknown>>(row: T): T =>
  ({
    ...row,
    ...Object.fromEntries(DEAL_PROPOSAL_COLUMNS.map((c) => [c, null])),
  }) as T;

/** private.deal_proposal_base: package price + Σ qty × unit price. */
export const proposalBase = (
  packagePrice: number,
  addons: readonly { qty: number; unit_price: number }[],
): number => monthlyTotal(packagePrice, addons);
/** private.deal_proposal_annual: round(base × 12 × (100 − discount) %, 2). */
export const proposalAnnual = (
  packagePrice: number,
  addons: readonly { qty: number; unit_price: number }[],
  discountPct: number,
): number => annualAmount(proposalBase(packagePrice, addons), discountPct);
/** private.deal_proposal_mrr: monthly = base; annual = round(annual price / 12, 2). */
export const proposalMrr = (
  billing: Billing,
  packagePrice: number,
  addons: readonly { qty: number; unit_price: number }[],
  discountPct: number | null,
): number =>
  billing === "annual"
    ? annualMrr(proposalAnnual(packagePrice, addons, discountPct ?? 0))
    : proposalBase(packagePrice, addons);
/** round(MRR) half away from zero: the deal's amount (bigint, whole dollars). */
export const proposalAmount = (mrr: number): number =>
  Math.sign(mrr) * Math.round(Math.abs(mrr));

export type ProposalPackage = {
  id: number;
  line: string;
  monthly_price: number;
  active: boolean;
};
export type ProposalAddon = {
  code: string;
  line: string | null;
  unit_price: number;
  active: boolean;
};
export type ProposalCatalog = {
  packages: readonly ProposalPackage[];
  addons: readonly ProposalAddon[];
  /** private.annual_discount_pct() */
  discountPct: number;
};
/** A refusal of the price trigger: invalid_package | invalid_addons | invalid_billing (the SQL message). */
export class ProposalError extends Error {
  constructor(
    public readonly code:
      | "invalid_package"
      | "invalid_addons"
      | "invalid_billing"
      | "deal_closed",
    public readonly detail: string = "",
  ) {
    super(code);
  }
}

const num = (v: unknown): number => Number(v);
const choiceOf = (addons: unknown): string =>
  JSON.stringify(
    (Array.isArray(addons) ? addons : [])
      .map((a) => ({
        code: (a as { code?: unknown })?.code ?? null,
        qty: (a as { qty?: unknown })?.qty ?? null,
      }))
      .sort((x, y) => String(x.code).localeCompare(String(y.code))),
  );

/** 70_'s private.subscription_addons_snapshot: [{code, qty}] → [{code, qty, unit_price}]. */
const addonsSnapshot = (
  input: unknown,
  line: string,
  keep: readonly AddonSnapshot[],
  catalog: readonly ProposalAddon[],
): AddonSnapshot[] => {
  if (input == null) return [];
  if (!Array.isArray(input) || input.length > 20)
    throw new ProposalError("invalid_addons");
  const out: AddonSnapshot[] = [];
  for (const e of input as unknown[]) {
    const code = String((e as { code?: unknown })?.code ?? "");
    const qty = (e as { qty?: unknown })?.qty;
    if (
      typeof e !== "object" ||
      e === null ||
      typeof qty !== "number" ||
      !Number.isInteger(qty) ||
      qty < 1 ||
      qty > 9999 ||
      out.some((x) => x.code === code)
    )
      throw new ProposalError("invalid_addons", code);
    const kept = keep.find((k) => k.code === code);
    const price =
      kept?.unit_price ??
      catalog.find(
        (a) =>
          a.code === code && a.active && (a.line == null || a.line === line),
      )?.unit_price;
    if (price == null) throw new ProposalError("invalid_addons", code);
    out.push({ code, qty, unit_price: num(price) });
  }
  return out;
};

/**
 * public.deals_proposal_price: the proposal and amount to store, from the stored row (`old`, null on insert), the row
 * being written (`next`: the client's proposal) and the catalog. No package: the proposal is cleared and the typed
 * amount stands. Unchanged (same package, billing and add-on choice): the snapshot stands. Else the catalog of the day
 * for what is new (an add-on already there keeps its unit price; the stored package keeps its price). `reprice`: the
 * whole catalog of the day (« Mettre à jour au prix actuel »).
 */
export const priceProposal = (
  old: StoredProposal | null,
  next: Partial<Record<DealProposalColumn, unknown>> & {
    amount?: unknown;
    stage?: unknown;
  },
  catalog: ProposalCatalog,
  reprice = false,
): DealProposal & { amount: number | null | undefined } => {
  const packageId =
    next.proposed_package_id == null || next.proposed_package_id === ""
      ? null
      : num(next.proposed_package_id);
  // a won or lost deal keeps its proposal (deal_closed; reopen it first)
  const closed = (st: unknown) => st === "gagne" || st === "perdu";
  const before = old ?? {};
  const beforePackage =
    before.proposed_package_id == null ? null : num(before.proposed_package_id);
  if (
    !reprice &&
    old &&
    closed(old.stage) &&
    closed(next.stage ?? old.stage) &&
    (packageId !== beforePackage ||
      (packageId !== null &&
        ((next.proposed_billing ?? "monthly") !== before.proposed_billing ||
          choiceOf(next.proposed_addons ?? []) !==
            choiceOf(before.proposed_addons ?? []))))
  )
    throw new ProposalError("deal_closed");
  if (packageId === null)
    return {
      ...NO_PROPOSAL,
      amount:
        next.amount == null
          ? (next.amount as null | undefined)
          : Number(next.amount),
    };
  const billing = (next.proposed_billing ?? "monthly") as Billing;
  if (billing !== "monthly" && billing !== "annual")
    throw new ProposalError("invalid_billing");
  const o = old ?? {};
  const oldPackage =
    o.proposed_package_id == null ? null : num(o.proposed_package_id);
  const oldAddons = [...(o.proposed_addons ?? [])];
  let price: number;
  let addons: AddonSnapshot[];
  let discount: number | null;
  if (
    !reprice &&
    oldPackage !== null &&
    packageId === oldPackage &&
    billing === o.proposed_billing &&
    choiceOf(next.proposed_addons ?? []) === choiceOf(oldAddons)
  ) {
    price = num(o.proposed_package_price);
    addons = oldAddons;
    discount =
      o.proposed_discount_pct == null ? null : num(o.proposed_discount_pct);
  } else {
    const pkg = catalog.packages.find(
      (p) =>
        num(p.id) === packageId &&
        (p.active || (!reprice && packageId === oldPackage)),
    );
    if (!pkg) throw new ProposalError("invalid_package");
    const oldLine = catalog.packages.find(
      (p) => num(p.id) === oldPackage,
    )?.line;
    const same = !reprice && packageId === oldPackage;
    price = same ? num(o.proposed_package_price) : num(pkg.monthly_price);
    addons = addonsSnapshot(
      next.proposed_addons ?? [],
      pkg.line,
      !reprice && oldLine === pkg.line ? oldAddons : [],
      catalog.addons,
    );
    discount =
      billing !== "annual"
        ? null
        : !reprice &&
            o.proposed_billing === "annual" &&
            o.proposed_discount_pct != null
          ? num(o.proposed_discount_pct)
          : catalog.discountPct;
  }
  const mrr = proposalMrr(billing, price, addons, discount);
  return {
    proposed_package_id: packageId,
    proposed_billing: billing,
    proposed_addons: addons,
    proposed_package_price: price,
    proposed_discount_pct: discount,
    proposed_mrr: mrr,
    amount: proposalAmount(mrr),
  };
};
